import { axios } from 'utils/axios';
import { useMutation, UseMutationResult } from '@tanstack/react-query';
import { EducationStatus, School, User } from '@novaheal/types';
import { useUser } from 'utils/auth';
import { toastContent } from 'config/toast';
import { useNovaToast } from 'hooks/utils/useNovaToast';
import { EmailStatusType, SuccessStatusType } from 'types/StatusTypes';

export type UpdateUserDto = {
  firstName: string;
  lastName: string;
  b2bSchool?: School;
  b2cSchool: School;
  educationStatus: EducationStatus;
  email?: string;
  examination?: {
    month: number;
    year: number;
  } | null;
  examinationDateUnknown?: boolean;
  marketingEmailOptIn?: boolean;
};

export const updateUserData = async (data: UpdateUserDto): Promise<User> => {
  return axios.patch(`user/me`, { ...data });
};

export const useUpdateUser = (): UseMutationResult<User, unknown, UpdateUserDto, unknown> => {
  const { refetch: refetchUser } = useUser();
  const toast = useNovaToast();
  return useMutation({
    // TODO(error-handling): Remove suppress once PATCH /user/me returns ERROR_CODES codeError
    // for invalid school name (422). Use meta.expectedCodeErrors instead.
    // See frontend/docs/error-handling.md → Backend error contract.
    meta: { suppressGlobalMutationError: true },
    onSuccess: () => {
      toast.show(toastContent.success[SuccessStatusType.CHANGED_DATA]);
      refetchUser();
    },
    mutationFn: updateUserData,
  });
};

export const updateUserEmail = async (email: string): Promise<'USED' | 'NOT USED'> => {
  return axios.post(`user/email`, { email });
};

export const useUpdateUserEmail = (
  showToast = true
): UseMutationResult<'USED' | 'NOT USED', unknown, string, unknown> => {
  const { refetch: refetchUser } = useUser();
  const toast = useNovaToast();
  return useMutation({
    // TODO(error-handling): Remove suppress once POST /user/email returns ERROR_CODES codeError
    // for social-login block (400 NOT_ALLOWED). Use meta.expectedCodeErrors instead.
    // See frontend/docs/error-handling.md → Backend error contract.
    meta: { suppressGlobalMutationError: true },
    onSuccess: (data: 'USED' | 'NOT USED') => {
      if (showToast) {
        if (data === 'USED') toast.show(toastContent.email[EmailStatusType.TAKEN]);
        else toast.show(toastContent.success[SuccessStatusType.CHANGED_DATA]);
      }
      refetchUser();
    },
    mutationFn: updateUserEmail,
  });
};
